'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import { tmdb } from '@/lib/tmdb';
import { BuddyService, BuddyLink } from '@/lib/buddies';
import { RecommendationService } from '@/lib/recommendations';
import { buildProgressSnapshot } from '@/lib/buddy-progress';
import { MediaPickerDialog } from '@/components/MediaPickerDialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Users, Link2, X, Tv, Film } from 'lucide-react';

// Watch Together overview panel.
// - Invite links are reusable: several friends can accept the same link, and
//   each gets a PRIVATE pairwise link with you (friends never see each other).
// - Active links are grouped by show: one card per show, one bar per friend.
// - "You" is computed from the live local watchlist, and stale snapshots are
//   repaired on load via reconcileMyProgress.

export function WatchTogetherPanel() {
  const { user } = useAuth();
  const { watchlist, updateWatchlistItem } = useStore();
  const [buddies, setBuddies] = useState<BuddyLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // Which full-screen picker is open, if any
  const [pickerMode, setPickerMode] = useState<'tv' | 'movie' | null>(null);
  const [recCreating, setRecCreating] = useState(false);

  const loadBuddies = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const links = await BuddyService.listBuddies();
      setBuddies(links);
      // Repair stale self-progress (e.g. show added/watched after accepting)
      BuddyService.reconcileMyProgress(links, user.uid, watchlist, updateWatchlistItem);
    } catch (error) {
      // Silent
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    loadBuddies();
  }, [loadBuddies]);

  // TV shows eligible for a new invite (on watchlist, not hidden)
  const allInvitableShows = watchlist.filter(item => item.type === 'tv' && !item.hiddenFromSharing);

  // Movies eligible for a recommendation (on watchlist, not hidden)
  const allRecommendableMovies = watchlist.filter(item => item.type === 'movie' && !item.hiddenFromSharing);

  // Open invites I created (reusable links), grouped by invite
  const openInvites = useMemo(() => {
    if (!user) return [];
    const groups = new Map<string, { groupId: string; shows: BuddyLink[]; createdAt: string }>();
    for (const buddy of buddies) {
      if (buddy.status !== 'pending' || buddy.createdBy !== user.uid) continue;
      const key = buddy.inviteGroupId || buddy.id;
      const group = groups.get(key) || { groupId: key, shows: [], createdAt: buddy.createdAt };
      group.shows.push(buddy);
      groups.set(key, group);
    }
    return Array.from(groups.values()).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [buddies, user]);

  // Active pairwise links grouped by show → one card per show, a bar per friend
  const linkedShows = useMemo(() => {
    if (!user) return [];
    const byShow = new Map<number, { showId: number; showName: string; posterPath: string | null; friends: Array<{ linkId: string; name: string; progress: any }> }>();
    for (const buddy of buddies) {
      if (buddy.status !== 'active') continue;
      const otherUid = buddy.participants.find(p => p !== user.uid);
      if (!otherUid) continue;
      const entry = byShow.get(buddy.showId) || {
        showId: buddy.showId,
        showName: buddy.showName,
        posterPath: buddy.posterPath,
        friends: [],
      };
      entry.friends.push({
        linkId: buddy.id,
        name: buddy.names?.[otherUid] || 'Friend',
        progress: buddy.progress?.[otherUid] || null,
      });
      byShow.set(buddy.showId, entry);
    }
    return Array.from(byShow.values()).sort((a, b) => a.showName.localeCompare(b.showName));
  }, [buddies, user]);

  // Called by the picker dialog; throws on failure so the dialog stays open
  // with the selection intact
  const handleInvite = async (ids: number[], username?: string) => {
    setCreating(true);
    try {
      const result = await BuddyService.createInvite(ids, username);
      await navigator.clipboard.writeText(result.link);
      if (result.notified) {
        toast.success(`Invite sent to ${result.notifiedName} — link also copied for anyone else!`);
      } else if (result.warning) {
        toast.warning(`${result.warning}. Link copied instead.`);
      } else {
        toast.success(
          ids.length === 1
            ? 'Invite link copied — anyone who opens it links with you privately!'
            : `Invite link for ${ids.length} shows copied — one link covers them all!`
        );
      }
      await loadBuddies();
    } catch (e: any) {
      toast.error(e.message || 'Failed to create invite');
      throw e;
    } finally {
      setCreating(false);
    }
  };

  const handleRecommend = async (ids: number[], username?: string) => {
    setRecCreating(true);
    try {
      const result = await RecommendationService.createRec(ids, username);
      await navigator.clipboard.writeText(result.link);
      if (result.notified) {
        toast.success(`Recommendation sent to ${result.notifiedName} — link also copied for anyone else!`);
      } else if (result.warning) {
        toast.warning(`${result.warning}. Link copied instead.`);
      } else {
        toast.success(
          ids.length === 1
            ? 'Recommendation link copied — friends can add the movie in one tap!'
            : `Recommendation link for ${ids.length} movies copied — friends pick which ones to add!`
        );
      }
    } catch (e: any) {
      toast.error(e.message || 'Failed to create recommendation');
      throw e;
    } finally {
      setRecCreating(false);
    }
  };

  const handleCopyInvite = (groupId: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/buddy/${groupId}`);
    toast.success('Invite link copied');
  };

  const handleRevokeInvite = async (groupId: string) => {
    try {
      await BuddyService.revokeInvite(groupId);
      await loadBuddies();
      toast.success('Invite revoked');
    } catch (error) {
      toast.error('Failed to revoke invite');
    }
  };

  const handleUnlinkFriend = async (linkId: string) => {
    try {
      await BuddyService.leave(linkId);
      await loadBuddies();
      toast.success('Progress unlinked');
    } catch (error) {
      toast.error('Failed to unlink');
    }
  };

  if (!user) return null;

  return (
    <div>
      <p className="text-gray-400 text-sm mb-6">
        Link with friends on individual shows to compare episode progress. Invite links are
        reusable — every friend who accepts gets a <span className="text-gray-300">private</span> link
        with you, and friends never see each other&apos;s progress. Only the shows you explicitly
        share are visible, never your full watchlist. You can also recommend movies — friends
        pick which ones land on their watchlist.
      </p>

      {/* New invite + movie recommendations */}
      <div className="flex flex-wrap gap-3 mb-8">
      <div className="relative inline-block">
        <Button
          onClick={() => {
            setShowPicker(!showPicker);
            setShowMoviePicker(false);
            setPickerQuery('');
            setSelectedShows(new Set());
            setInviteUsername('');
          }}
          disabled={creating || allInvitableShows.length === 0}
          className="h-11 rounded-full bg-red-600 hover:bg-red-500 px-5"
        >
          <Link2 className="h-4 w-4 mr-2" />
          {creating ? 'Creating invite...' : 'New invite'}
          <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showPicker ? 'rotate-180' : ''}`} />
        </Button>
        {showPicker && (
          <div className="absolute z-20 top-full mt-2 w-[min(90vw,22rem)] rounded-xl bg-gray-900 ring-1 ring-white/15 shadow-2xl overflow-hidden">
            {/* Search within your shows */}
            <div className="relative p-2 border-b border-white/10">
              <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              <input
                autoFocus
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                placeholder="Search your shows..."
                className="w-full h-9 rounded-lg bg-white/5 ring-1 ring-white/10 border-0 pl-9 pr-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/60"
              />
            </div>

            {/* Multi-select show list */}
            <div className="max-h-56 overflow-y-auto">
              {invitableShows.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">No shows match &quot;{pickerQuery}&quot;</p>
              ) : (
                invitableShows.map((show) => {
                  const isSelected = selectedShows.has(show.id);
                  return (
                    <button
                      key={show.id}
                      onClick={() => toggleShow(show.id)}
                      className={`w-full flex items-center gap-3 p-2.5 transition-colors text-left ${
                        isSelected ? 'bg-red-600/15' : 'hover:bg-white/5'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? 'bg-red-600' : 'ring-1 ring-white/25'
                        }`}
                      >
                        {isSelected && <CheckIcon className="h-3.5 w-3.5 text-white" />}
                      </div>
                      <div className="w-8 h-12 rounded overflow-hidden relative flex-shrink-0 bg-gray-800">
                        {show.posterPath && (
                          <Image src={tmdb.getImageUrl(show.posterPath, 'w200')} alt="" fill sizes="32px" className="object-cover" />
                        )}
                      </div>
                      <span className="text-sm text-white truncate">{show.name}</span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Optional direct delivery + create */}
            <div className="p-2 border-t border-white/10 space-y-2">
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
                <input
                  value={inviteUsername}
                  onChange={(e) => setInviteUsername(e.target.value)}
                  placeholder="Send to username (optional)"
                  className="w-full h-9 rounded-lg bg-white/5 ring-1 ring-white/10 border-0 pl-8 pr-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/60"
                />
              </div>
              <Button
                onClick={handleInvite}
                disabled={selectedShows.size === 0}
                className="w-full h-9 rounded-lg bg-red-600 hover:bg-red-500 text-sm"
              >
                {selectedShows.size === 0 ? (
                  'Select shows to invite'
                ) : inviteUsername.trim() ? (
                  <><Send className="h-3.5 w-3.5 mr-1.5" /> Send invite ({selectedShows.size} show{selectedShows.size !== 1 ? 's' : ''})</>
                ) : (
                  <><Link2 className="h-3.5 w-3.5 mr-1.5" /> Create invite link ({selectedShows.size} show{selectedShows.size !== 1 ? 's' : ''})</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Recommend movies — friends pick which ones to add to their watchlist */}
      <div className="relative inline-block">
        <Button
          onClick={() => {
            setShowMoviePicker(!showMoviePicker);
            setShowPicker(false);
            setMoviePickerQuery('');
            setSelectedMovies(new Set());
            setRecUsername('');
          }}
          disabled={recCreating || allRecommendableMovies.length === 0}
          variant="outline"
          className="h-11 rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white px-5"
        >
          <Film className="h-4 w-4 mr-2" />
          {recCreating ? 'Creating link...' : 'Recommend movies'}
          <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showMoviePicker ? 'rotate-180' : ''}`} />
        </Button>
        {showMoviePicker && (
          <div className="absolute z-20 top-full mt-2 w-[min(90vw,22rem)] rounded-xl bg-gray-900 ring-1 ring-white/15 shadow-2xl overflow-hidden">
            {/* Search within your movies */}
            <div className="relative p-2 border-b border-white/10">
              <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              <input
                autoFocus
                value={moviePickerQuery}
                onChange={(e) => setMoviePickerQuery(e.target.value)}
                placeholder="Search your movies..."
                className="w-full h-9 rounded-lg bg-white/5 ring-1 ring-white/10 border-0 pl-9 pr-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/60"
              />
            </div>

            {/* Multi-select movie list */}
            <div className="max-h-56 overflow-y-auto">
              {recommendableMovies.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">No movies match &quot;{moviePickerQuery}&quot;</p>
              ) : (
                recommendableMovies.map((movie) => {
                  const isSelected = selectedMovies.has(movie.id);
                  return (
                    <button
                      key={movie.id}
                      onClick={() => toggleMovie(movie.id)}
                      className={`w-full flex items-center gap-3 p-2.5 transition-colors text-left ${
                        isSelected ? 'bg-red-600/15' : 'hover:bg-white/5'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? 'bg-red-600' : 'ring-1 ring-white/25'
                        }`}
                      >
                        {isSelected && <CheckIcon className="h-3.5 w-3.5 text-white" />}
                      </div>
                      <div className="w-8 h-12 rounded overflow-hidden relative flex-shrink-0 bg-gray-800">
                        {movie.posterPath && (
                          <Image src={tmdb.getImageUrl(movie.posterPath, 'w200')} alt="" fill sizes="32px" className="object-cover" />
                        )}
                      </div>
                      <span className="text-sm text-white truncate">{movie.name}</span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Optional direct delivery + create */}
            <div className="p-2 border-t border-white/10 space-y-2">
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
                <input
                  value={recUsername}
                  onChange={(e) => setRecUsername(e.target.value)}
                  placeholder="Send to username (optional)"
                  className="w-full h-9 rounded-lg bg-white/5 ring-1 ring-white/10 border-0 pl-8 pr-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/60"
                />
              </div>
              <Button
                onClick={handleRecommend}
                disabled={selectedMovies.size === 0}
                className="w-full h-9 rounded-lg bg-red-600 hover:bg-red-500 text-sm"
              >
                {selectedMovies.size === 0 ? (
                  'Select movies to recommend'
                ) : recUsername.trim() ? (
                  <><Send className="h-3.5 w-3.5 mr-1.5" /> Send recommendation ({selectedMovies.size} movie{selectedMovies.size !== 1 ? 's' : ''})</>
                ) : (
                  <><Link2 className="h-3.5 w-3.5 mr-1.5" /> Create link ({selectedMovies.size} movie{selectedMovies.size !== 1 ? 's' : ''})</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Open invites */}
      {openInvites.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-3">Open Invites</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {openInvites.map((invite) => (
              <div key={invite.groupId} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 flex items-center gap-4">
                <div className="flex -space-x-3 flex-shrink-0">
                  {invite.shows.slice(0, 3).map((show, i) => (
                    <div key={show.id} className="w-10 h-14 rounded-md overflow-hidden ring-2 ring-black relative bg-gray-800" style={{ zIndex: 3 - i }}>
                      {show.posterPath && (
                        <Image src={tmdb.getImageUrl(show.posterPath, 'w200')} alt="" fill sizes="40px" className="object-cover" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-60" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500" />
                    </span>
                    {invite.shows.length} show{invite.shows.length !== 1 ? 's' : ''} · reusable link
                  </p>
                  <p className="text-[11px] text-gray-500 line-clamp-1 mb-1.5">
                    {invite.shows.map(s => s.showName).join(', ')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopyInvite(invite.groupId)}
                      className="inline-flex items-center gap-1 rounded-full bg-white/10 ring-1 ring-white/20 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-white/20 transition-colors"
                    >
                      <Link2 className="h-3 w-3" /> Copy link
                    </button>
                    <button
                      onClick={() => handleRevokeInvite(invite.groupId)}
                      className="inline-flex items-center gap-1 rounded-full bg-red-950/40 ring-1 ring-red-500/30 px-2.5 py-1 text-[11px] font-semibold text-red-400 hover:bg-red-950/70 transition-colors"
                    >
                      <X className="h-3 w-3" /> Revoke
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Linked shows — one card per show, a race bar per friend */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-2xl" />
          ))}
        </div>
      ) : linkedShows.length === 0 ? (
        <div className="rounded-2xl bg-gradient-to-br from-gray-900/80 to-black ring-1 ring-white/10 text-center py-16 px-6">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-red-600/15 ring-1 ring-red-500/30 flex items-center justify-center">
            <Users className="h-9 w-9 text-red-500" />
          </div>
          <p className="text-lg font-semibold text-white mb-1">No linked shows yet</p>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            Create an invite above, send the link to any number of friends, and race each other
            episode by episode.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {linkedShows.map((show) => {
            // "You" always reflects the live local watchlist, not a snapshot
            const localItem = watchlist.find(i => i.id === show.showId && i.type === 'tv');
            const myProgress = localItem ? buildProgressSnapshot(localItem) : null;
            const myEps = myProgress?.episodesWatched || 0;
            const leader = Math.max(myEps, ...show.friends.map(f => f.progress?.episodesWatched || 0), 1);

            const progressLabel = (p: any) => {
              if (p?.lastEpisode) return `${p.lastEpisode} · ${p.episodesWatched} eps`;
              if (p?.watchedSeasons?.length) return `${p.watchedSeasons.length} season${p.watchedSeasons.length !== 1 ? 's' : ''} watched`;
              return 'Not started';
            };

            return (
              <div
                key={show.showId}
                className="group relative overflow-hidden rounded-2xl ring-1 ring-white/10 hover:ring-red-500/50 transition-all duration-300 bg-gray-900"
              >
                {show.posterPath && (
                  <Image
                    src={tmdb.getImageUrl(show.posterPath, 'w500')}
                    alt=""
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover object-top opacity-25 blur-[2px] scale-110 group-hover:opacity-35 transition-opacity duration-300"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />

                <div className="relative p-4 flex gap-4">
                  <Link href={`/tv/${show.showId}`} className="flex-shrink-0">
                    <div className="w-16 h-24 rounded-lg overflow-hidden relative bg-gray-800 ring-1 ring-white/15 shadow-xl transition-transform duration-300 group-hover:scale-105">
                      {show.posterPath ? (
                        <Image src={tmdb.getImageUrl(show.posterPath, 'w200')} alt={show.showName} fill sizes="64px" className="object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                          <Tv className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className="flex-1 min-w-0">
                    <Link href={`/tv/${show.showId}`} className="font-bold tracking-tight text-white hover:text-red-400 transition-colors line-clamp-1 drop-shadow-lg">
                      {show.showName}
                    </Link>

                    <div className="mt-2.5 space-y-2">
                      {/* You */}
                      <div>
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-[11px] font-semibold text-white">You</span>
                          <span className="text-[11px] text-gray-400">{progressLabel(myProgress)}</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-500"
                            style={{ width: `${(myEps / leader) * 100}%` }}
                          />
                        </div>
                      </div>

                      {/* Each friend gets their own private bar */}
                      {show.friends.map((friend) => {
                        const theirEps = friend.progress?.episodesWatched || 0;
                        return (
                          <div key={friend.linkId} className="group/friend">
                            <div className="flex items-baseline justify-between mb-1">
                              <span className="text-[11px] font-semibold text-gray-300 truncate max-w-[50%] flex items-center gap-1.5">
                                {friend.name}
                                <button
                                  onClick={() => handleUnlinkFriend(friend.linkId)}
                                  className="opacity-0 group-hover/friend:opacity-100 text-gray-500 hover:text-red-400 transition-opacity"
                                  title={`Unlink ${friend.name}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                              <span className="text-[11px] text-gray-400">{progressLabel(friend.progress)}</span>
                            </div>
                            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-gray-400 to-white transition-all duration-500"
                                style={{ width: `${(theirEps / leader) * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}

                      {/* Verdict vs the closest competitor */}
                      {(() => {
                        const best = Math.max(...show.friends.map(f => f.progress?.episodesWatched || 0));
                        const diff = Math.abs(myEps - best);
                        return (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold backdrop-blur-md ring-1 ${
                            myEps === best
                              ? 'bg-white/10 text-gray-200 ring-white/20'
                              : myEps > best
                                ? 'bg-red-600/20 text-red-300 ring-red-500/40'
                                : 'bg-yellow-600/15 text-yellow-300 ring-yellow-500/30'
                          }`}>
                            {myEps === best
                              ? "Dead even — who's watching tonight?"
                              : myEps > best
                                ? `You're ${diff} episode${diff !== 1 ? 's' : ''} ahead`
                                : `You're ${diff} episode${diff !== 1 ? 's' : ''} behind`}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
